// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { UserRole } from '../../types/auth';
import './Sidebar.scss';

interface SidebarItem {
  path: string;
  label: string;
  icon: string;
  requiredRole?: UserRole;
  children?: SidebarItem[];
}

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: (collapsed: boolean) => void;
  isMobile?: boolean;
  mobileMenuOpen?: boolean;
  onMobileMenuClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isCollapsed: externalIsCollapsed, 
  onToggle,
  isMobile = false,
  mobileMenuOpen = false,
  onMobileMenuClose
}) => {
  const location = useLocation();
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>(['/container-settings']);
  
  // Use external state if provided, otherwise use internal state
  const isCollapsed = externalIsCollapsed !== undefined ? externalIsCollapsed : internalIsCollapsed;
  const username = authService.getUsername();
  const userRole = authService.getSessionRole();

  const sidebarItems: SidebarItem[] = [
    {
      path: '/',
      label: 'Dashboard',
      icon: 'dashboard',
      requiredRole: UserRole.USER_ROLE_MODIFIER,
    },
    {
      path: '/ad-management',
      label: 'Ad Management',
      icon: 'ads',
      requiredRole: UserRole.USER_ROLE_MODIFIER,
    },
    {
      path: '/shop',
      label: 'Shop',
      icon: 'coins',
      requiredRole: UserRole.USER_ROLE_MODIFIER,
    },
    {
      path: '/container-settings',
      label: 'Investaplay Settings',
      icon: 'configuration',
      requiredRole: UserRole.USER_ROLE_MODIFIER,
      children: [
        {
          path: '/quiz-management',
          label: 'Quiz Management',
          icon: 'quiz',
          requiredRole: UserRole.USER_ROLE_MODIFIER,
        },
        {
          path: '/ludo-settings',
          label: 'Ludo Game',
          icon: 'ludo',
          requiredRole: UserRole.USER_ROLE_MODIFIER,
        },
        {
          path: '/solitaire-settings',
          label: 'Solitaire Game',
          icon: 'solitaire',
          requiredRole: UserRole.USER_ROLE_MODIFIER,
        }
      ]
    },
    {
      path: '/events',
      label: 'Events',
      icon: 'notification',
      requiredRole: UserRole.USER_ROLE_MODIFIER,
    },
    {
      path: '/send-notification',
      label: 'Send Notification',
      icon: 'chat',
      requiredRole: UserRole.USER_ROLE_MODIFIER,
    },
    {
      path: '/users',
      label: 'User Management',
      icon: 'user-management',
      requiredRole: UserRole.USER_ROLE_ADMIN,
    },
    {
      path: '/accounts',
      label: 'Players',
      icon: 'accounts',
      requiredRole: UserRole.USER_ROLE_MODIFIER,
    },
  ];

  const handleLogout = async () => {
    try {
      await authService.logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      // Force logout even if API call fails
      window.location.href = '/login';
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isParentActive = (item: SidebarItem) => {
    if (isActive(item.path)) return true;
    if (item.children) {
      return item.children.some(child => isActive(child.path));
    }
    return false;
  };

  const toggleExpanded = (path: string) => {
    setExpandedItems(prev => 
      prev.includes(path) 
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  const hasPermission = (requiredRole?: UserRole) => {
    if (!requiredRole) return true;
    // Admin has access to everything, modifier has access to modifier-level features
    if (userRole === UserRole.USER_ROLE_ADMIN) return true;
    if (userRole === UserRole.USER_ROLE_MODIFIER && requiredRole === UserRole.USER_ROLE_MODIFIER) return true;
    return false;
  };

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    if (onToggle) {
      onToggle(newCollapsedState);
    } else {
      setInternalIsCollapsed(newCollapsedState);
    }
  };

  const handleLinkClick = () => {
    if (isMobile && onMobileMenuClose) {
      onMobileMenuClose();
    }
  };

  return (
    <nav className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <Link to="/" className="brand-link">
            <img 
              className="logo-icon" 
              src="/static/logo.png" 
              alt="Nakama" 
              width="192" 
              height="" 
            />
          </Link>
        </div>
      </div>

      <div className="sidebar-separator" />

      <div className="sidebar-nav">
        <ul className="sidebar-menu">
          {sidebarItems
            .filter(item => hasPermission(item.requiredRole))
            .map((item) => (
            <li key={item.path} className="sidebar-menu-item">
              {item.children ? (
                <>
                  <div className="parent-item">
                    <Link
                      to={item.path}
                      className={`sidebar-link parent-link ${isParentActive(item) ? 'active' : ''}`}
                      title={isCollapsed ? item.label : ''}
                      onClick={handleLinkClick}
                    >
                      <img 
                        className="sidebar-icon" 
                        src={`/static/svg/${item.icon}.svg`} 
                        alt="" 
                        width="24" 
                        height="" 
                      />
                      <span className="sidebar-label">{item.label}</span>
                    </Link>
                    <button
                      className="expand-button"
                      onClick={() => toggleExpanded(item.path)}
                      title={expandedItems.includes(item.path) ? 'Collapse' : 'Expand'}
                    >
                      <span 
                        className={`expand-icon ${expandedItems.includes(item.path) ? 'expanded' : ''}`}
                      >
                        ▼
                      </span>
                    </button>
                  </div>
                  {expandedItems.includes(item.path) && (
                    <ul className="sidebar-submenu">
                      {item.children
                        .filter(child => hasPermission(child.requiredRole))
                        .map((child) => (
                        <li key={child.path} className="sidebar-submenu-item">
                          <Link
                            to={child.path}
                            className={`sidebar-sublink ${isActive(child.path) ? 'active' : ''}`}
                            title={isCollapsed ? child.label : ''}
                            onClick={handleLinkClick}
                          >
                            <img 
                              className="sidebar-subicon" 
                              src={`/static/svg/${child.icon}.svg`} 
                              alt="" 
                              width="20" 
                              height="" 
                            />
                            <span className="sidebar-sublabel">{child.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  title={isCollapsed ? item.label : ''}
                  onClick={handleLinkClick}
                >
                  <img 
                    className="sidebar-icon" 
                    src={`/static/svg/${item.icon}.svg`} 
                    alt="" 
                    width="24" 
                    height="" 
                  />
                  <span className="sidebar-label">{item.label}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>

        
        <div className="sidebar-separator" />
        
        {/* User Section */}
        <div className="logged-in-as">
          Logged in as: {username}
        </div>
        
        <ul className="sidebar-menu">
          <li className="sidebar-menu-item">
            <button 
              onClick={handleLogout}
              className="logout-link"
              title="Logout"
            >
              <img className="logout-icon" src="/static/svg/log-out.svg" alt="" width="24" height="" />
              <span className="logout-text">Logout</span>
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Sidebar;
